import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Camera, Upload as UploadCloudIcon, Video, CloudUpload, AlertCircle, Play } from 'lucide-react'
import { Modal } from '../Modal'
import { Button } from '../Button'
import { Text } from '../Text'
import * as s from './MediaUploader.style.css'
import {
  uid,
  isHttpUrl,
  acceptForMediaType,
  isValidFileForMediaType,
  invalidFileMessage,
  uploadFailedMessage,
  YOUTUBE_NOT_ALLOWED_MESSAGE,
  type MediaUploaderMediaType,
} from './logic/mediaUploaderUtils'
import { captureVideoThumbnailFromUrl } from '@/app/lib/media/captureVideoThumbnail'
import {
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  isYoutubeUrl,
} from '@/app/lib/media/youtube'
import { YoutubeIcon } from '../Icon'
import { useT } from "../../../app/lib/i18n";

// ─── Public types ───────────────────────────────────────────────────────────────

export interface MediaEntry {
  id: string
  url: string
  /** Thumbnail/poster image. For video without one, a placeholder icon is shown instead. */
  previewUrl?: string
  /** Mid-upload-to-storage. */
  uploading?: boolean
}

export type MediaUploaderShape = 'rectangle' | 'square' | 'circle'

export interface MediaUploaderExtraAction {
  label: string
  icon?: React.ReactNode
  /** Optional small preview image shown next to the label. Clicking the row also stages this URL. */
  previewUrl?: string
  onClick: () => void
  /** Default true; caller can hide conditionally. */
  visible?: boolean
}

export interface MediaUploaderProps {
  mediaType?: 'image' | 'video'
  /** Controlled list of items. Pass a single-item array for avatar/logo mode. */
  items: MediaEntry[]
  /**
   * Fires for display-section-only mutations that don't need the modal/Apply flow:
   * reorder, single-item delete, bulk delete, and the "remove" action (single mode).
   * NOT fired for new uploads — those go through onApply.
   */
  onChange: (items: MediaEntry[]) => void
  /**
   * Fires when the modal's Apply button is clicked (or a fast-path drag-and-drop upload
   * finishes), with the full new items array to merge in — single mode: the one
   * replacement item; multi mode: existing items + newly staged ones, in final order.
   * The component awaits this (if it returns a promise) and shows a per-item "unsaved"
   * indicator on the newly-applied items until it resolves; on rejection, keeps the
   * indicator in an error state and surfaces the rejection message via onError.
   */
  onApply: (newOrChangedItems: MediaEntry[]) => void | Promise<void>
  /** Consumer-supplied upload function. Receives a File, returns the hosted URL (or { url, previewUrl?, id? }). */
  uploadFile: (file: File) => Promise<string | { url: string; previewUrl?: string; id?: string }>
  /**
   * Optional: re-host a pasted http(s) image URL immediately (same UX as file upload).
   * When provided for image mode, paste of a link uploads via this callback instead of hotlinking.
   * When omitted, URL paste stays hotlinked until Apply (avatar/video flows).
   */
  uploadFromUrl?: (url: string) => Promise<string | { url: string; previewUrl?: string; id?: string }>
  /** Max number of items allowed. Default 1 (single-item mode). */
  maxItems?: number
  shape?: MediaUploaderShape
  /**
   * Single-item mode only: override the fixed box size (px) for `circle`/`square`
   * shapes. Defaults to 128 (circle) / 140 (square). `rectangle` shape ignores this —
   * it already sizes to its container.
   */
  previewSize?: number
  /**
   * Single-item `rectangle` mode only: CSS aspect-ratio for the upload/preview box
   * (e.g. `"420 / 280"` or `"3 / 2"`). Defaults to the component's min-height sizing.
   */
  aspectRatio?: string
  /**
   * Generalized per-thumbnail badge for multi-item mode (e.g. caller renders a "Main"
   * badge when index === 0). Rendered bottom-left, matching the legacy "Main" position.
   */
  renderBadge?: (entry: MediaEntry, index: number) => React.ReactNode
  /** Single-item mode only: fixed corner badge (bottom-right), e.g. the Google-account badge. */
  badge?: React.ReactNode
  /** Rendered in the upload modal below the paste hint (generalizes "Reset to Google avatar"). */
  extraActions?: MediaUploaderExtraAction[]
  /** Explicit "remove/clear" action for single-item mode. Only rendered when provided and an item exists. */
  onRemove?: (id: string) => void
  selectable?: boolean
  onBulkDelete?: (ids: string[]) => void
  onDeleteRequest?: (id: string) => void
  error?: string | null
  onError?: (err: string | null) => void
  placeholder?: string
  /**
   * Empty-state display-section hint (e.g. "Drag & drop or click to browse"). The
   * "or Ctrl+V…" paste hint now lives inside the modal and is not part of this text.
   */
  hint?: string
  maxRows?: number
  flashingIds?: Map<string, number>
  /** When true, hides add/upload, delete, select, and drag-reorder. */
  readOnly?: boolean
  /** Master on/off switch for paste support. The modal-open scoping is automatic/internal. */
  pasteEnabled?: boolean
  /**
   * When true (video mode), Ctrl+V can paste a YouTube watch/short/embed URL.
   * The video stays hotlinked on YouTube; a YouTube poster + corner badge are shown.
   */
  allowYoutube?: boolean
  /** Fires when a URL is pasted or staged in the upload modal (before Apply). */
  onStagedUrl?: (url: string) => void
  /** Fires when a poster frame is captured from a pasted video URL (before Apply). */
  onStagedVideoThumbnail?: (blob: Blob | null) => void
  /** Fires when the upload modal closes without Apply (Cancel, backdrop, Escape). */
  onModalDismiss?: () => void
}

// ─── Internal (non-controlled) staging types ─────────────────────────────────────

interface StagedItem {
  id: string
  file?: File
  url?: string
  previewUrl?: string
  uploading: boolean
  error?: string
  /** Seeded from the current single-mode item — already "resolved", no upload needed. */
  isExisting?: boolean
  /** Real id returned by uploadFile(), if different from the local staging id. */
  finalId?: string
}

type PendingState = 'pending' | 'error'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveUpload(
  result: string | { url: string; previewUrl?: string; id?: string },
  fallbackId: string,
) {
  const url = typeof result === 'string' ? result : result.url
  const id = typeof result === 'object' && result.id ? result.id : fallbackId
  const previewUrl = typeof result === 'object' ? result.previewUrl : undefined
  return { url, id, previewUrl }
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  const t = useT();
  return <div className={s.spinner} aria-label={t("shared.media.uploading")} />
}

// ─── Small inline icons (kept as raw SVG to match the legacy ImageUpload look) ──

function DropIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function XIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function CheckIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function TrashIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  )
}

// ─── Thumbnail visual (handles image vs. video-without-poster) ─────────────────

function isVideoSourceUrl(url: string) {
  if (isYoutubeUrl(url)) return false
  return isHttpUrl(url) || url.startsWith('blob:')
}

function YoutubeCornerBadge() {
  const t = useT()
  return (
    <span className={s.providerBadge} title={t("shared.media.youtubeVideo")} aria-label={t("shared.media.youtubeVideo")}>
      <YoutubeIcon size={16} />
    </span>
  )
}

function ThumbVisual({
  mediaType,
  url,
  previewUrl,
  className,
  placeholderClassName,
  placeholderSize = 24,
  alt = '',
  loading = false,
  onPreviewReady,
}: {
  mediaType: MediaUploaderMediaType
  url: string
  previewUrl?: string
  className: string
  placeholderClassName: string
  placeholderSize?: number
  alt?: string
  loading?: boolean
  onPreviewReady?: () => void
}) {
  const [videoReady, setVideoReady] = useState(false)

  useEffect(() => {
    setVideoReady(false)
  }, [url, previewUrl])

  if (mediaType === 'video') {
    const youtubeId = url ? getYoutubeVideoId(url) : null
    const effectivePreview =
      previewUrl ?? (youtubeId ? getYoutubeThumbnailUrl(youtubeId) : undefined)
    if (effectivePreview) {
      return (
        <img
          src={effectivePreview}
          alt={alt}
          className={className}
          onLoad={() => onPreviewReady?.()}
        />
      )
    }
    if (url && isVideoSourceUrl(url)) {
      return (
        <div className={s.thumbVideoFrame}>
          <video
            src={url}
            className={className}
            muted
            playsInline
            preload="auto"
            onLoadedData={() => {
              setVideoReady(true)
              onPreviewReady?.()
            }}
            onError={() => onPreviewReady?.()}
          />
          {loading && !videoReady && (
            <div className={s.stagedTileOverlay}>
              <Spinner />
            </div>
          )}
        </div>
      )
    }
    return (
      <div className={placeholderClassName}>
        <Video size={placeholderSize} aria-hidden />
      </div>
    )
  }

  if (!url) {
    return (
      <div className={placeholderClassName}>
        <Video size={placeholderSize} aria-hidden />
      </div>
    )
  }
  return <img src={url} alt={alt} className={className} />
}

// ─── Thumbnail hover preview popover ─────────────────────────────────────────

const PREVIEW_MAX_W = 200
const PREVIEW_GAP = 10

function ThumbPreviewPopover({ mediaType, url, previewUrl, rect }: { mediaType: MediaUploaderMediaType; url: string; previewUrl?: string; rect: DOMRect }) {
  const vw = window.innerWidth
  const vh = window.innerHeight

  let left = rect.left + rect.width / 2 - PREVIEW_MAX_W / 2
  left = Math.max(8, Math.min(left, vw - PREVIEW_MAX_W - 8))

  const spaceAbove = rect.top - PREVIEW_GAP
  const showAbove = spaceAbove > 80

  const style: React.CSSProperties = {
    left,
    maxWidth: PREVIEW_MAX_W,
    ...(showAbove
      ? { bottom: vh - rect.top + PREVIEW_GAP }
      : { top: rect.bottom + PREVIEW_GAP }),
  }

  const src = mediaType === 'video' ? previewUrl : (previewUrl ?? url)

  return (
    <div className={s.thumbPreview} style={style}>
      {src ? (
        <img src={src} alt="" className={s.thumbPreviewImg} />
      ) : (
        <div className={s.thumbPreviewPlaceholder}><Video size={28} aria-hidden /></div>
      )}
    </div>
  )
}

// ─── Animation durations ─────────────────────────────────────────────────────

const ENTER_MS = 300
const EXIT_MS = 230

// ─── Main component ───────────────────────────────────────────────────────────

export function MediaUploader({
  mediaType = 'image',
  items,
  onChange,
  onApply,
  uploadFile,
  uploadFromUrl,
  maxItems = 1,
  shape = 'rectangle',
  previewSize,
  aspectRatio,
  renderBadge,
  badge,
  extraActions,
  onRemove,
  error,
  onError,
  pasteEnabled = true,
  allowYoutube = false,
  onStagedUrl,
  onStagedVideoThumbnail,
  onModalDismiss,
  placeholder,
  hint,
  onDeleteRequest,
  selectable,
  onBulkDelete,
  maxRows = 3,
  flashingIds,
  readOnly = false,
}: MediaUploaderProps) {
  const t = useT()
  const resolvedPlaceholder =
    placeholder ??
    (mediaType === 'video'
      ? t('shared.media.dropVideo')
      : t('shared.media.dropImage'))
  const itemsRef = useRef(items)
  itemsRef.current = items

  const isSingle = maxItems === 1
  const canAddMore = !readOnly && (isSingle || items.length < maxItems)
  const selectionEnabled = Boolean(selectable) && !readOnly

  // Shared shape/size math — used by both the single-item display below and the
  // modal's staged tiles, so modal thumbnails visually match the `shape` prop too.
  const isShaped = shape === 'circle' || shape === 'square'
  const shapedSize = previewSize ?? (shape === 'circle' ? 128 : 140)
  const radius = shape === 'circle' ? '50%' : 10

  // ── Modal + staging state ─────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false)
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false)
  const [videoPreviewSrc, setVideoPreviewSrc] = useState<string | null>(null)
  const [stagedItems, setStagedItems] = useState<StagedItem[]>([])
  const stagedItemsRef = useRef<StagedItem[]>([])
  const snapshotAtOpenRef = useRef<{ url?: string; previewUrl?: string } | null>(null)
  const stagedFileInputRef = useRef<HTMLInputElement>(null)
  const activeSlotIdRef = useRef<string | null>(null)
  const applyCommittedRef = useRef(false)

  const updateStaged = useCallback((updater: (prev: StagedItem[]) => StagedItem[]) => {
    const next = updater(stagedItemsRef.current)
    stagedItemsRef.current = next
    setStagedItems(next)
  }, [])

  const ensureTrailingSlot = useCallback((list: StagedItem[]): StagedItem[] => {
    if (isSingle) return list
    const occupied = list.filter(i => i.file || i.url || i.uploading).length
    const hasCapacity = items.length + occupied < maxItems
    const last = list[list.length - 1]
    const lastIsEmpty = !!last && !last.file && !last.url && !last.uploading
    if (hasCapacity && !lastIsEmpty) return [...list, { id: uid(), uploading: false }]
    if (!hasCapacity && lastIsEmpty) return list.slice(0, -1)
    return list
  }, [isSingle, items.length, maxItems])

  // ── Fast-path (drag-directly-onto-button) in-flight display placeholders ──────
  const [localStaging, setLocalStaging] = useState<Map<string, true>>(new Map())

  // ── "Unsaved" pending/error indicator, keyed by (post-apply) entry id ─────────
  const [pendingMap, setPendingMap] = useState<Map<string, PendingState>>(new Map())
  const [pendingErrors, setPendingErrors] = useState<Map<string, string>>(new Map())

  const markPending = useCallback((ids: string[]) => {
    if (!ids.length) return
    setPendingMap(prev => { const m = new Map(prev); ids.forEach(id => m.set(id, 'pending')); return m })
  }, [])
  const clearPending = useCallback((ids: string[]) => {
    if (!ids.length) return
    setPendingMap(prev => { const m = new Map(prev); ids.forEach(id => m.delete(id)); return m })
    setPendingErrors(prev => { const m = new Map(prev); ids.forEach(id => m.delete(id)); return m })
  }, [])
  const markPendingError = useCallback((ids: string[], message: string) => {
    if (!ids.length) return
    setPendingMap(prev => { const m = new Map(prev); ids.forEach(id => m.set(id, 'error')); return m })
    setPendingErrors(prev => { const m = new Map(prev); ids.forEach(id => m.set(id, message)); return m })
    onError?.(message)
  }, [onError])

  const applyWithPendingTracking = useCallback((payload: MediaEntry[], ids: string[]) => {
    let result: void | Promise<void>
    try {
      result = onApply(payload)
    } catch (err) {
      markPendingError(ids, err instanceof Error ? err.message : 'Failed to save')
      return
    }
    if (result && typeof (result as Promise<void>).then === 'function') {
      markPending(ids)
      ;(result as Promise<void>).then(
        () => clearPending(ids),
        (err) => markPendingError(ids, err instanceof Error ? err.message : 'Failed to save'),
      )
    }
  }, [onApply, markPending, clearPending, markPendingError])

  // ── Open / close modal ────────────────────────────────────────────────────────

  const openModal = useCallback(() => {
    onError?.(null)
    if (isSingle) {
      const current = items[0]
      snapshotAtOpenRef.current = current
        ? { url: current.url, previewUrl: current.previewUrl }
        : null
      const seed: StagedItem = current
        ? { id: current.id, url: current.url, previewUrl: current.previewUrl, uploading: false, isExisting: true }
        : { id: uid(), uploading: false }
      stagedItemsRef.current = [seed]
      setStagedItems([seed])
    } else {
      snapshotAtOpenRef.current = null
      const seed = ensureTrailingSlot([])
      stagedItemsRef.current = seed
      setStagedItems(seed)
    }
    setModalOpen(true)
  }, [isSingle, items, ensureTrailingSlot, onError])

  const closeModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  const openVideoPreview = useCallback((event: React.MouseEvent, src: string) => {
    event.stopPropagation()
    event.preventDefault()
    setVideoPreviewSrc(src)
    setVideoPreviewOpen(true)
  }, [])

  const closeVideoPreview = useCallback((open: boolean) => {
    setVideoPreviewOpen(open)
    if (!open) setVideoPreviewSrc(null)
  }, [])

  // Discard staged content whenever the modal closes, whether via Cancel, Escape,
  // backdrop click, or the built-in close button — but not after a successful Apply.
  useEffect(() => {
    if (!modalOpen) {
      if (!applyCommittedRef.current) onModalDismiss?.()
      applyCommittedRef.current = false
      stagedItemsRef.current = []
      setStagedItems([])
    }
  }, [modalOpen, onModalDismiss])

  // ── Staged upload execution ───────────────────────────────────────────────────

  const runStagedUpload = useCallback(async (id: string, file: File) => {
    try {
      const result = await uploadFile(file)
      const { url, id: finalId, previewUrl } = resolveUpload(result, id)
      updateStaged(prev => prev.map(it => it.id === id ? {
        ...it,
        uploading: false,
        url,
        finalId,
        previewUrl: previewUrl ?? it.previewUrl,
        error: undefined,
      } : it))
    } catch (err) {
      const msg = err instanceof Error ? err.message : uploadFailedMessage(mediaType)
      updateStaged(prev => prev.map(it => it.id === id ? { ...it, uploading: false, error: msg } : it))
      onError?.(msg)
    }
  }, [uploadFile, updateStaged, onError, mediaType])

  const runStagedUrlUpload = useCallback(async (id: string, remoteUrl: string) => {
    if (!uploadFromUrl) return
    try {
      const result = await uploadFromUrl(remoteUrl)
      const { url, id: finalId, previewUrl } = resolveUpload(result, id)
      updateStaged(prev => prev.map(it => it.id === id ? {
        ...it,
        uploading: false,
        url,
        finalId,
        previewUrl: previewUrl ?? it.previewUrl,
        error: undefined,
      } : it))
    } catch (err) {
      const msg = err instanceof Error ? err.message : uploadFailedMessage(mediaType)
      updateStaged(prev => prev.map(it => it.id === id ? { ...it, uploading: false, error: msg } : it))
      onError?.(msg)
    }
  }, [uploadFromUrl, updateStaged, onError, mediaType])

  const stageFiles = useCallback((files: File[], targetSlotId: string | null) => {
    const valid = files.filter(f => isValidFileForMediaType(f, mediaType))
    if (!valid.length) {
      onError?.(invalidFileMessage(mediaType))
      return
    }
    onError?.(null)

    const toUpload: { id: string; file: File }[] = []
    updateStaged(prev => {
      let next = [...prev]
      let remaining = [...valid]

      const targetIdx = targetSlotId ? next.findIndex(it => it.id === targetSlotId) : -1
      if (targetIdx >= 0 && remaining.length) {
        const file = remaining.shift()!
        const localPreviewUrl = mediaType === 'video' ? URL.createObjectURL(file) : undefined
        next[targetIdx] = {
          ...next[targetIdx],
          file,
          uploading: true,
          url: undefined,
          previewUrl: localPreviewUrl,
          error: undefined,
          isExisting: false,
        }
        toUpload.push({ id: next[targetIdx].id, file })
      }

      if (!isSingle) {
        for (const file of remaining) {
          const occupied = next.filter(it => it.file || it.url || it.uploading).length
          if (items.length + occupied >= maxItems) break
          const id = uid()
          const localPreviewUrl = mediaType === 'video' ? URL.createObjectURL(file) : undefined
          next.push({ id, file, uploading: true, previewUrl: localPreviewUrl })
          toUpload.push({ id, file })
        }
        next = ensureTrailingSlot(next)
      }

      return next
    })

    toUpload.forEach(({ id, file }) => void runStagedUpload(id, file))
  }, [mediaType, onError, updateStaged, isSingle, items.length, maxItems, ensureTrailingSlot, runStagedUpload])

  const stageUrl = useCallback((url: string) => {
    onError?.(null)
    onStagedUrl?.(url)

    const youtubeId = mediaType === 'video' ? getYoutubeVideoId(url) : null
    const youtubeThumb = youtubeId ? getYoutubeThumbnailUrl(youtubeId) : undefined
    const shouldRehostImage = mediaType === 'image' && !!uploadFromUrl && isHttpUrl(url)

    const current = stagedItemsRef.current
    const targetId = isSingle
      ? current[0]?.id
      : current.find(it => !it.file && !it.url && !it.uploading)?.id

    let stagedSlotId: string | undefined

    updateStaged(prev => {
      let next = [...prev]
      const slotId = targetId ?? (isSingle ? next[0]?.id : undefined)

      if (slotId) {
        stagedSlotId = slotId
        next = next.map(it => it.id === slotId
          ? {
            ...it,
            url,
            previewUrl: youtubeThumb,
            uploading: shouldRehostImage || (mediaType === 'video' && !youtubeThumb),
            file: undefined,
            error: undefined,
            isExisting: false,
          }
          : it)
      } else if (!isSingle) {
        const id = uid()
        stagedSlotId = id
        next = [...next, {
          id,
          url,
          previewUrl: youtubeThumb,
          uploading: shouldRehostImage || (mediaType === 'video' && !youtubeThumb),
        }]
      }

      if (!isSingle) next = ensureTrailingSlot(next)
      return next
    })

    if (shouldRehostImage && stagedSlotId) {
      void runStagedUrlUpload(stagedSlotId, url)
      return
    }

    const resolvedTargetId = targetId ?? (isSingle ? stagedItemsRef.current[0]?.id : undefined)
    if (mediaType !== 'video' || !isHttpUrl(url) || !resolvedTargetId) {
      return
    }

    // YouTube posters are hotlinked — skip HTML5 frame capture (CORS fails on YT).
    if (youtubeThumb) {
      onStagedVideoThumbnail?.(null)
      return
    }

    void captureVideoThumbnailFromUrl(url)
      .then((blob) => {
        onStagedVideoThumbnail?.(blob)
        const posterUrl = URL.createObjectURL(blob)
        updateStaged(prev => prev.map(it => (
          it.id === resolvedTargetId && it.url === url
            ? { ...it, previewUrl: posterUrl, uploading: false, error: undefined }
            : it
        )))
      })
      .catch(() => {
        onStagedVideoThumbnail?.(null)
        updateStaged(prev => prev.map(it => (
          it.id === resolvedTargetId && it.url === url
            ? { ...it, uploading: false }
            : it
        )))
      })
  }, [mediaType, isSingle, onError, onStagedUrl, onStagedVideoThumbnail, updateStaged, ensureTrailingSlot, uploadFromUrl, runStagedUrlUpload])

  const markStagedPreviewReady = useCallback((id: string) => {
    updateStaged(prev => prev.map(it => (
      it.id === id && it.uploading ? { ...it, uploading: false } : it
    )))
  }, [updateStaged])

  const openPickerForSlot = (slotId: string) => {
    activeSlotIdRef.current = slotId
    stagedFileInputRef.current?.click()
  }

  const handleStagedFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (!files.length) return
    stageFiles(files, activeSlotIdRef.current)
  }

  const removeStagedTile = (id: string) => {
    updateStaged(prev => {
      let next = prev.filter(it => it.id !== id)
      if (!next.length) next = [{ id: uid(), uploading: false }]
      if (!isSingle) next = ensureTrailingSlot(next)
      return next
    })
  }

  // ── Apply ──────────────────────────────────────────────────────────────────────

  const anyStagedUploading = stagedItems.some(it => it.uploading)
  const hasResolvedStagedContent = stagedItems.some(it => !!it.url)
  // Single mode allows an "empty" Apply when there's a pre-existing item to remove
  // (the modal's "Remove image" action clears the staged slot rather than applying
  // immediately, so the user can still Cancel out of it) — otherwise empty means
  // nothing to save yet, keep Apply disabled.
  const canConfirmRemoval = isSingle && !!items[0] && !stagedItems[0]?.url && !anyStagedUploading
  const applyDisabled = anyStagedUploading || (!hasResolvedStagedContent && !canConfirmRemoval)

  const handleApply = () => {
    if (applyDisabled) return

    if (isSingle) {
      const seeded = stagedItems[0]
      const existing = items[0]

      if (!seeded?.url) {
        // Staged slot was cleared via "Remove image" — confirm the removal.
        applyCommittedRef.current = true
        closeModal()
        if (existing && onRemove) onRemove(existing.id)
        return
      }
      if (seeded.isExisting) {
        closeModal()
        return
      }

      const snapshot = snapshotAtOpenRef.current
      if (
        snapshot
        && seeded.url === snapshot.url
        && seeded.previewUrl === snapshot.previewUrl
        && !seeded.file
      ) {
        closeModal()
        return
      }

      const finalItem: MediaEntry = {
        id: seeded.finalId ?? seeded.id,
        url: seeded.url,
        previewUrl: seeded.previewUrl,
      }
      applyCommittedRef.current = true
      closeModal()
      applyWithPendingTracking([finalItem], [finalItem.id])
    } else {
      const existingIds = new Set(items.map(i => i.id))
      const newlyUploaded: MediaEntry[] = stagedItems
        .filter(it => it.url && !it.isExisting)
        .map(it => ({ id: it.finalId ?? it.id, url: it.url!, previewUrl: it.previewUrl }))
        .filter(it => !existingIds.has(it.id))
      // Uploads may already be in `items` via websocket reload — treat as committed apply.
      applyCommittedRef.current = true
      closeModal()
      if (!newlyUploaded.length) return
      const payload = [...items, ...newlyUploaded]
      applyWithPendingTracking(payload, newlyUploaded.map(i => i.id))
    }
  }

  // ── Fast-path: drag a file directly onto the Add tile / single box ───────────────

  const fastUpload = useCallback(async (files: File[]) => {
    const valid = files.filter(f => isValidFileForMediaType(f, mediaType))
    if (!valid.length) {
      onError?.(invalidFileMessage(mediaType))
      return
    }
    onError?.(null)
    const capRemaining = isSingle ? 1 : Math.max(maxItems - itemsRef.current.length, 0)
    const toUpload = valid.slice(0, capRemaining)
    if (!toUpload.length) return

    for (const file of toUpload) {
      const localId = uid()
      setLocalStaging(prev => { const m = new Map(prev); m.set(localId, true); return m })
      try {
        const result = await uploadFile(file)
        const { url, id: finalId, previewUrl } = resolveUpload(result, localId)
        setLocalStaging(prev => { const m = new Map(prev); m.delete(localId); return m })
        const newItem: MediaEntry = { id: finalId, url, previewUrl }
        const payload = isSingle ? [newItem] : [...itemsRef.current, newItem]
        applyWithPendingTracking(payload, [finalId])
      } catch (err) {
        setLocalStaging(prev => { const m = new Map(prev); m.delete(localId); return m })
        onError?.(err instanceof Error ? err.message : uploadFailedMessage(mediaType))
      }
    }
  }, [mediaType, onError, isSingle, maxItems, uploadFile, applyWithPendingTracking])

  // ── Display-level drag & drop (fast path only — click opens the modal instead) ──

  const [dragOver, setDragOver] = useState(false)
  const handleDisplayDragOver = (e: React.DragEvent) => {
    if (readOnly || dragSrcIdx !== null) return
    e.preventDefault()
    setDragOver(true)
  }
  const handleDisplayDragLeave = () => setDragOver(false)
  const handleDisplayDrop = (e: React.DragEvent) => {
    if (readOnly || dragSrcIdx !== null) return
    e.preventDefault()
    setDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (!files.length) return
    void fastUpload(files)
  }

  // ── Paste (Ctrl+V) — active only while the modal is open ──────────────────────

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const dtItems = e.clipboardData?.items
    if (dtItems) {
      for (const item of dtItems) {
        if (mediaType === 'image' && item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          const targetSlotId = isSingle
            ? stagedItemsRef.current[0]?.id ?? null
            : stagedItemsRef.current.find(it => !it.file && !it.url && !it.uploading)?.id ?? null
          if (file) stageFiles([file], targetSlotId)
          return
        }
        // Pasting raw video bytes is rarely supported by OS clipboards, but handle it
        // when present since ClipboardItem exposes a real video/* MIME type.
        if (mediaType === 'video' && item.type.startsWith('video/')) {
          e.preventDefault()
          const file = item.getAsFile()
          const targetSlotId = isSingle
            ? stagedItemsRef.current[0]?.id ?? null
            : stagedItemsRef.current.find(it => !it.file && !it.url && !it.uploading)?.id ?? null
          if (file) stageFiles([file], targetSlotId)
          return
        }
      }
    }

    const text = e.clipboardData?.getData('text/plain')?.trim()
    if (text && isHttpUrl(text)) {
      e.preventDefault()
      if (mediaType === 'video' && isYoutubeUrl(text) && !allowYoutube) {
        onError?.(YOUTUBE_NOT_ALLOWED_MESSAGE)
        return
      }
      stageUrl(text)
    }
  }, [mediaType, isSingle, allowYoutube, onError, stageFiles, stageUrl])

  useEffect(() => {
    if (!modalOpen || !pasteEnabled) return
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [modalOpen, pasteEnabled, handlePaste])

  // ── Extra actions (e.g. "Reset to Google avatar") ─────────────────────────────

  const handleExtraAction = (action: MediaUploaderExtraAction) => {
    if (action.previewUrl) stageUrl(action.previewUrl)
    action.onClick()
  }

  // ── Multi-item mode: reorder / select / bulk-delete / hover-preview state ────

  const scrollRef = useRef<HTMLDivElement>(null)
  const [dragSrcIdx, setDragSrcIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const [dragGhost, setDragGhost] = useState<{ mediaType: MediaUploaderMediaType; url: string; previewUrl?: string; w: number; h: number } | null>(null)
  const [dragGhostPos, setDragGhostPos] = useState({ x: 0, y: 0 })
  const dragGrabOffset = useRef({ x: 0, y: 0 })
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const [hoverPreview, setHoverPreview] = useState<{ url: string; previewUrl?: string; rect: DOMRect } | null>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Enter / exit animation state ─────────────────────────────────────────────
  const [removingItems, setRemovingItems] = useState<Map<string, MediaEntry>>(new Map())
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set())
  const prevItemsRef = useRef<MediaEntry[]>(items)
  const prevFlashingIdsRef = useRef<Map<string, number>>(new Map())

  // Single-item mode: a brief shimmer sweep when the displayed image/video actually
  // changes — same visual language as the multi-item gallery's flashingIds sweep, just
  // driven internally instead of by an external prop (there's only ever one slot here).
  const [singleJustUpdated, setSingleJustUpdated] = useState(false)
  const prevSingleUrlRef = useRef<string | undefined>(items[0]?.url)
  useEffect(() => {
    const nextUrl = items[0]?.url
    const prevUrl = prevSingleUrlRef.current
    prevSingleUrlRef.current = nextUrl
    if (nextUrl && prevUrl !== undefined && nextUrl !== prevUrl) {
      setSingleJustUpdated(true)
      const t = setTimeout(() => setSingleJustUpdated(false), 750)
      return () => clearTimeout(t)
    }
  }, [items])

  useEffect(() => {
    const prev = prevItemsRef.current
    prevItemsRef.current = items
    const prevIds = new Set(prev.map(i => i.id))
    const nextIds = new Set(items.map(i => i.id))

    const added = items.filter(i => !prevIds.has(i.id) && !i.uploading).map(i => i.id)
    if (added.length > 0) {
      setEnteringIds(p => { const set = new Set(p); added.forEach(id => set.add(id)); return set })
      setTimeout(() => setEnteringIds(p => { const set = new Set(p); added.forEach(id => set.delete(id)); return set }), ENTER_MS)
    }

    const removed = prev.filter(i => !nextIds.has(i.id) && !i.uploading)
    if (removed.length > 0) {
      setRemovingItems(p => { const map = new Map(p); removed.forEach(item => map.set(item.id, item)); return map })
      setTimeout(() => setRemovingItems(p => { const map = new Map(p); removed.forEach(item => map.delete(item.id)); return map }), EXIT_MS)
    }
  }, [items])

  useEffect(() => {
    if (!flashingIds?.size) return
    const prev = prevFlashingIdsRef.current
    const newIds: string[] = []
    flashingIds.forEach((gen, id) => {
      if ((prev.get(id) ?? -1) < gen) newIds.push(id)
    })
    prevFlashingIdsRef.current = new Map(flashingIds)
    if (!newIds.length) return
    setEnteringIds(p => { const set = new Set(p); newIds.forEach(id => set.add(id)); return set })
    setTimeout(() => setEnteringIds(p => { const set = new Set(p); newIds.forEach(id => set.delete(id)); return set }), ENTER_MS)
  }, [flashingIds])

  const localStagingEntries: MediaEntry[] = useMemo(
    () => Array.from(localStaging.keys()).map(id => ({ id, url: '', uploading: true })),
    [localStaging],
  )

  const displayedItems = useMemo(() => {
    const removing = Array.from(removingItems.values()).filter(r => !items.some(i => i.id === r.id))
    return [...items, ...removing, ...localStagingEntries]
  }, [items, removingItems, localStagingEntries])

  // ── Core reorder / select / delete handlers (unchanged from legacy ImageUpload) ──

  const dragOverIdxRef = useRef<number | null>(null)
  dragOverIdxRef.current = dragOverIdx

  useEffect(() => {
    if (dragSrcIdx === null) return

    const onPointerMove = (e: PointerEvent) => {
      setDragGhostPos({ x: e.clientX, y: e.clientY })
      const el = document.elementFromPoint(e.clientX, e.clientY)
      const thumbEl = el?.closest('[data-drag-idx]') as HTMLElement | null
      const over = thumbEl ? parseInt(thumbEl.dataset.dragIdx ?? '-1') : -1
      setDragOverIdx(over >= 0 ? over : null)
    }

    const onPointerUp = () => {
      document.documentElement.style.removeProperty('cursor')
      const src = dragSrcIdx
      const over = dragOverIdxRef.current
      setDragSrcIdx(null)
      setDragOverIdx(null)
      setDragGhost(null)
      if (src !== null && over !== null && src !== over) {
        const next = [...itemsRef.current]
        const [item] = next.splice(src, 1)
        next.splice(over, 0, item)
        onChange(next)
        requestAnimationFrame(() => {
          const lo = Math.min(src, over)
          const hi = Math.max(src, over)
          const movedIds = next.slice(lo, hi + 1).map(i => i.id)
          setEnteringIds(p => { const set = new Set(p); movedIds.forEach(id => set.add(id)); return set })
          setTimeout(() => setEnteringIds(p => { const set = new Set(p); movedIds.forEach(id => set.delete(id)); return set }), ENTER_MS)
        })
      }
    }

    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
    return () => {
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
    }
  }, [dragSrcIdx, onChange])

  const handleThumbPointerDown = (idx: number) => (e: React.PointerEvent) => {
    if (items[idx].uploading) return
    e.preventDefault()
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    setHoverPreview(null)

    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const w = Math.round(rect.width)
    const h = Math.round(rect.height)

    dragGrabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    document.documentElement.style.setProperty('cursor', 'grabbing', 'important')

    setDragSrcIdx(idx)
    const item = items[idx]
    if (!item.uploading && (item.url || item.previewUrl)) {
      setDragGhost({ mediaType, url: item.url, previewUrl: item.previewUrl, w, h })
      setDragGhostPos({ x: e.clientX, y: e.clientY })
    }
  }

  const handleDelete = (id: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onDeleteRequest) {
      onDeleteRequest(id)
    } else {
      onChange(items.filter(item => item.id !== id))
    }
  }

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds)
    setSelectedIds(new Set())
    if (onBulkDelete) {
      onBulkDelete(ids)
    } else {
      onChange(items.filter(item => !ids.includes(item.id)))
    }
  }, [selectedIds, onBulkDelete, onChange, items])

  const handleThumbPointerEnter = useCallback((item: MediaEntry) => (e: React.PointerEvent) => {
    if (dragSrcIdx !== null || item.uploading) return
    if (mediaType === 'image' && !item.url) return
    if (mediaType === 'video' && !item.previewUrl) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    hoverTimerRef.current = setTimeout(() => setHoverPreview({ url: item.url, previewUrl: item.previewUrl, rect }), 800)
  }, [dragSrcIdx, mediaType])

  const handleThumbPointerLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    setHoverPreview(null)
  }, [])

  useEffect(() => {
    const itemIds = new Set(items.map(item => item.id))
    setSelectedIds(prev => {
      const stale = Array.from(prev).filter(id => !itemIds.has(id))
      if (!stale.length) return prev
      const next = new Set(prev)
      stale.forEach(id => next.delete(id))
      return next
    })
  }, [items])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const update = () => {
      setShowTopFade(el.scrollTop > 4)
      setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 4)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [items.length])

  // ─────────────────────────────────────────────────────────────────────────────

  const pasteHintText = mediaType === 'video'
    ? (allowYoutube
      ? 'or Ctrl+V to paste a video or Youtube link…'
      : 'or Ctrl+V to paste a video link…')
    : 'or Ctrl+V to paste an image or link…'

  const resolveCornerBadge = (url: string | undefined) => {
    if (allowYoutube && url && isYoutubeUrl(url)) {
      return <YoutubeCornerBadge />
    }
    return badge
  }

  const visibleExtraActions = (extraActions ?? []).filter(a => a.visible !== false)
  // "Remove image/video" lives in the modal (alongside other actions like "Reset to
  // Google avatar") rather than on the inline display — only relevant in single-item
  // mode. Always mounted (never conditionally hidden) once `onRemove` is provided —
  // just disabled when there's nothing staged to remove — so the modal's height stays
  // stable as files are picked/removed instead of this row popping in and out.
  const showRemoveAction = isSingle && !!onRemove
  const removeActionDisabled = !stagedItems[0]?.url || !!stagedItems[0]?.uploading

  const modalTitle = isSingle
    ? (mediaType === 'video' ? 'Upload video' : 'Upload image')
    : (mediaType === 'video' ? 'Add videos' : 'Add images')

  const uploadModal = (
    <Modal open={modalOpen} setOpen={setModalOpen} minWidth="420px" maxWidth="560px" minHeight="auto">
      <div className={s.modalBody}>
        <div className={s.modalHeader}>
          <Text level="h3" weight="bold">{modalTitle}</Text>
        </div>

        <div className={s.stagedRow}>
          {stagedItems.map(staged => {
            const isEmpty = !staged.file && !staged.url && !staged.uploading
            if (isEmpty) {
              return (
                <button
                  key={staged.id}
                  type="button"
                  className={s.stagedTileEmpty}
                  style={{ borderRadius: radius }}
                  onClick={() => openPickerForSlot(staged.id)}
                  aria-label={mediaType === 'video' ? 'Upload video' : 'Upload image'}
                >
                  <UploadCloudIcon size={22} aria-hidden />
                  <span>{t("shared.media.upload")}</span>
                </button>
              )
            }
            // Multi-item mode already has a dedicated, always-present "+Add" tile for
            // adding more — so a filled staged tile here doesn't also need to be a
            // click-to-replace target with its own hover overlay; that's single-mode-only.
            const imgContent = (staged.url || staged.previewUrl || staged.uploading) && (
              <ThumbVisual
                mediaType={mediaType}
                url={staged.url ?? ''}
                previewUrl={staged.previewUrl}
                className={s.stagedTileImg}
                placeholderClassName={s.thumbVideoPlaceholder}
                loading={staged.uploading}
                onPreviewReady={() => markStagedPreviewReady(staged.id)}
              />
            )
            return (
              <div
                key={staged.id}
                className={`${s.stagedTileFilled}${staged.error ? ` ${s.stagedTileErrored}` : ''}`}
                style={{ borderRadius: radius }}
              >
                {staged.uploading && !staged.url && !staged.previewUrl ? (
                  <div className={s.stagedTileImgClip} style={{ cursor: 'default' }}>
                    <div className={s.thumbVideoPlaceholder}>
                      <Spinner />
                    </div>
                  </div>
                ) : isSingle ? (
                  <button
                    type="button"
                    className={s.stagedTileImgClip}
                    onClick={() => openPickerForSlot(staged.id)}
                    aria-label={mediaType === 'video' ? 'Replace video' : 'Replace image'}
                  >
                    {imgContent}
                    <div className={s.stagedTileHoverOverlay}>
                      <UploadCloudIcon size={18} aria-hidden />
                      <span>{t("shared.media.upload")}</span>
                    </div>
                  </button>
                ) : (
                  <div className={s.stagedTileImgClip} style={{ cursor: 'default' }}>
                    {imgContent}
                  </div>
                )}
                {(() => {
                  const cornerBadge = isSingle && !staged.uploading
                    ? resolveCornerBadge(staged.url)
                    : null
                  return cornerBadge
                    ? <span className={s.singleBadgeSlot}>{cornerBadge}</span>
                    : null
                })()}
                {staged.error && <div className={s.stagedTileErrorLabel}>{staged.error}</div>}
                <button
                  type="button"
                  className={s.stagedTileRemove}
                  onClick={() => removeStagedTile(staged.id)}
                  aria-label={t("shared.media.remove")}
                >
                  <XIcon />
                </button>
              </div>
            )
          })}
        </div>

        {pasteEnabled && <p className={s.hint}>{pasteHintText}</p>}

        {(showRemoveAction || visibleExtraActions.length > 0) && (
          <div className={s.extraActionsList}>
            {showRemoveAction && (
              <button
                type="button"
                className={`${s.extraActionRow}${removeActionDisabled ? ` ${s.extraActionRowDisabled}` : ''}`}
                disabled={removeActionDisabled}
                onClick={() => removeStagedTile(stagedItems[0].id)}
              >
                {mediaType === 'video' ? 'Remove video' : 'Remove image'}
              </button>
            )}
            {visibleExtraActions.map(action => (
              <button
                key={action.label}
                type="button"
                className={s.extraActionRow}
                onClick={() => handleExtraAction(action)}
              >
                {action.previewUrl && <img src={action.previewUrl} alt="" className={s.extraActionThumb} />}
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        )}

        {error && <p className={s.error}>{error}</p>}

        <div className={s.modalFooter}>
          <Button type="button" color="neutral" variant="ghost" onClick={closeModal}>
            Cancel
          </Button>
          <Button type="button" color="primary" variant="solid" disabled={applyDisabled} onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>

      <input
        ref={stagedFileInputRef}
        type="file"
        accept={acceptForMediaType(mediaType)}
        multiple={!isSingle}
        style={{ display: 'none' }}
        onChange={handleStagedFileInputChange}
        tabIndex={-1}
        aria-hidden
      />
    </Modal>
  )

  // ── Single-item mode ────────────────────────────────────────────────────────

  if (isSingle) {
    const current = items[0]
    const isUploading = !!current?.uploading || localStaging.size > 0
    const hasMedia = !!current && !isUploading && !!current.url
    const pendingState = current ? pendingMap.get(current.id) : undefined

    const wrapStyle: React.CSSProperties = isShaped
      ? { width: shapedSize, height: shapedSize, minHeight: 'unset', borderRadius: radius, alignSelf: 'center' }
      : {
          borderRadius: radius,
          ...(aspectRatio
            ? { aspectRatio, minHeight: 'unset', width: '100%' }
            : null),
        }

    // The 'pending' (in-flight save) state is intentionally not shown visually — the
    // underlying persist call is usually fast enough that the amber outline/badge only
    // ever reads as a confusing flash. 'error' (an actual failed save) still shows,
    // since that's a real, non-transient thing the user needs to notice.
    const clipClassName = [
      s.singlePreviewClip,
      dragOver ? s.singlePreviewOver : '',
      pendingState === 'error' ? s.singlePreviewErrored : '',
    ].filter(Boolean).join(' ')

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%' }}>
        <div
          role="button"
          tabIndex={0}
          className={s.singlePreviewWrap}
          style={wrapStyle}
          onClick={openModal}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openModal() }}
          onDragOver={handleDisplayDragOver}
          onDragLeave={handleDisplayDragLeave}
          onDrop={handleDisplayDrop}
          aria-label={mediaType === 'video' ? 'Upload video' : 'Upload image'}
        >
          <div className={clipClassName}>
            {isUploading ? (
              <div className={s.singlePreviewEmpty}><Spinner /></div>
            ) : hasMedia ? (
              <>
                <ThumbVisual
                  key={current.url}
                  mediaType={mediaType}
                  url={current.url}
                  previewUrl={current.previewUrl}
                  className={`${s.singlePreviewImg} ${s.singlePreviewImgFadeIn}`}
                  placeholderClassName={s.thumbVideoPlaceholder}
                  placeholderSize={isShaped ? 28 : 40}
                />
                  <div className={s.singlePreviewOverlay}>
                  {mediaType === 'video' && isVideoSourceUrl(current.url) && (
                    <button
                      type="button"
                      className={s.singlePreviewPlayBtn}
                      aria-label={t("shared.media.previewVideo")}
                      onClick={(event) => openVideoPreview(event, current.url)}
                    >
                      <Play size={18} aria-hidden />
                    </button>
                  )}
                  {mediaType === 'video' ? (
                    <Play size={18} aria-hidden />
                  ) : (
                    <Camera size={18} aria-hidden />
                  )}
                  {!isShaped && 'Change'}
                </div>
                {singleJustUpdated && <div key={current.url} className={s.thumbShimmer} aria-hidden="true" />}
              </>
            ) : (
              <div className={s.singlePreviewEmpty}>
                <DropIcon size={isShaped ? 20 : 28} />
                {!isShaped && <span className={s.dropzoneLabel}>{resolvedPlaceholder}</span>}
              </div>
            )}
          </div>
          {(() => {
            const cornerBadge = !isUploading ? resolveCornerBadge(current?.url) : null
            return cornerBadge
              ? <span className={s.singleBadgeSlot}>{cornerBadge}</span>
              : null
          })()}
          {pendingState === 'error' && (
            <span className={`${s.singlePendingBadge} ${s.singlePendingBadgeError}`}>
              <AlertCircle size={13} aria-hidden />
            </span>
          )}
        </div>

        {hint && <p className={s.hint}>{hint}</p>}
        {error && <p className={s.error}>{error}</p>}

        <Modal
          open={videoPreviewOpen}
          setOpen={closeVideoPreview}
          minWidth="min(720px, 92vw)"
          maxWidth="720px"
          minHeight="auto"
          closeOnClickOff
        >
          <div className={s.videoPreviewModalBody}>
            {videoPreviewSrc && (
              <video
                key={videoPreviewSrc}
                src={videoPreviewSrc}
                controls
                autoPlay
                playsInline
                className={s.videoPreviewPlayer}
              />
            )}
          </div>
        </Modal>

        {uploadModal}
      </div>
    )
  }

  // ── Multi-item mode ─────────────────────────────────────────────────────────

  const isDragActive = dragSrcIdx !== null
  const hasBulkBar = selectionEnabled && selectedIds.size > 0
  const thumbRowHeight = 96
  const gridGap = 8
  const scrollMaxHeight = maxRows * thumbRowHeight + (maxRows - 1) * gridGap

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      {dragGhost && (
        <div
          style={{
            position: 'fixed',
            left: dragGhostPos.x - dragGrabOffset.current.x,
            top: dragGhostPos.y - dragGrabOffset.current.y,
            width: dragGhost.w,
            height: dragGhost.h,
            borderRadius: 8,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 9999,
            transform: 'scale(1.08) rotate(2deg)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 4px 10px rgba(0,0,0,0.25)',
            transformOrigin: `${dragGrabOffset.current.x}px ${dragGrabOffset.current.y}px`,
          }}
        >
          <ThumbVisual
            mediaType={dragGhost.mediaType}
            url={dragGhost.url}
            previewUrl={dragGhost.previewUrl}
            className={s.stagedTileImg}
            placeholderClassName={s.thumbVideoPlaceholder}
          />
        </div>
      )}
      {hoverPreview && (
        <ThumbPreviewPopover mediaType={mediaType} url={hoverPreview.url} previewUrl={hoverPreview.previewUrl} rect={hoverPreview.rect} />
      )}
      {items.length === 0 ? (
        <div
          role="button"
          tabIndex={0}
          className={`${s.dropzone}${dragOver ? ` ${s.dropzoneOver}` : ''}`}
          onClick={openModal}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openModal() }}
          onDragOver={handleDisplayDragOver}
          onDragLeave={handleDisplayDragLeave}
          onDrop={handleDisplayDrop}
          aria-label={mediaType === 'video' ? 'Upload videos' : 'Upload images'}
        >
          <DropIcon />
          <span className={s.dropzoneLabel}>{resolvedPlaceholder}</span>
          <span className={s.dropzoneSub}>{hint ?? 'Drag & drop or click to browse'}</span>
        </div>
      ) : (
        <div className={s.galleryWrap}>
          <div
            ref={scrollRef}
            className={s.galleryScroll}
            style={{ maxHeight: scrollMaxHeight, padding: '8px 6px', paddingBottom: hasBulkBar ? 52 : 8 }}
          >
            <div className={[s.grid, isDragActive ? s.gridDragging : ''].filter(Boolean).join(' ')}>
              {displayedItems.map((item, idx) => {
                const isLive = idx < items.length
                const isRemoving = !isLive && removingItems.has(item.id)
                const isStaging = !isLive && !isRemoving
                const isEntering = enteringIds.has(item.id)
                const dragIdx = isLive ? idx : undefined
                const pendingState = isLive ? pendingMap.get(item.id) : undefined
                const flashGen = isLive ? flashingIds?.get(item.id) : undefined

                if (isStaging) {
                  return (
                    <div key={item.id} className={s.thumb}>
                      <div className={s.thumbUploading}><Spinner /></div>
                    </div>
                  )
                }

                return (
                  <div
                    key={item.id}
                    data-drag-idx={dragIdx}
                    className={[
                      s.thumb,
                      readOnly ? s.thumbReadOnly : '',
                      dragSrcIdx === idx ? s.thumbDragging : '',
                      dragOverIdx === idx && dragSrcIdx === idx ? s.thumbDragSrcHover : '',
                      dragOverIdx === idx && dragSrcIdx !== idx ? s.thumbDropTarget : '',
                      selectionEnabled && selectedIds.has(item.id) ? s.thumbChecked : '',
                      isEntering ? s.thumbEnter : '',
                      isRemoving ? s.thumbExit : '',
                      pendingState === 'pending' ? s.thumbPending : '',
                      pendingState === 'error' ? s.thumbErrored : '',
                    ].filter(Boolean).join(' ')}
                    onPointerDown={readOnly || isRemoving || !isLive ? undefined : handleThumbPointerDown(idx)}
                    onPointerEnter={isRemoving || !isLive ? undefined : handleThumbPointerEnter(item)}
                    onPointerLeave={isRemoving || !isLive ? undefined : handleThumbPointerLeave}
                  >
                    {item.uploading ? (
                      <div className={s.thumbUploading}><Spinner /></div>
                    ) : (
                      <ThumbVisual
                        mediaType={mediaType}
                        url={item.url}
                        previewUrl={item.previewUrl}
                        className={s.thumbImg}
                        placeholderClassName={s.thumbVideoPlaceholder}
                      />
                    )}
                    {flashGen != null && <div key={flashGen} className={s.thumbShimmer} aria-hidden="true" />}
                    {isLive && renderBadge && !item.uploading && (
                      <span className={s.thumbBadgeSlot}>{renderBadge(item, idx)}</span>
                    )}
                    {pendingState && (
                      <span className={`${s.thumbPendingBadge}${pendingState === 'error' ? ` ${s.thumbPendingBadgeError}` : ''}`}>
                        {pendingState === 'error' ? <AlertCircle size={11} aria-hidden /> : <CloudUpload size={11} aria-hidden />}
                      </span>
                    )}
                    {selectionEnabled && !item.uploading && (
                      <div
                        className={s.thumbCheck}
                        data-check="true"
                        onPointerDown={e => e.stopPropagation()}
                        onClick={e => { e.stopPropagation(); toggleSelect(item.id) }}
                        role="checkbox"
                        aria-checked={selectedIds.has(item.id)}
                        aria-label={t("shared.media.selectItem")}
                      >
                        {selectedIds.has(item.id) && <CheckIcon />}
                      </div>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        className={s.thumbDelete}
                        onPointerDown={e => e.stopPropagation()}
                        onClick={handleDelete(item.id)}
                        aria-label={t("shared.media.removeItem")}
                      >
                        <XIcon />
                      </button>
                    )}
                  </div>
                )
              })}

              {canAddMore && (
                <button
                  type="button"
                  className={`${s.addTile}${dragOver ? ` ${s.addTileOver}` : ''}`}
                  onClick={openModal}
                  onDragOver={handleDisplayDragOver}
                  onDragLeave={handleDisplayDragLeave}
                  onDrop={handleDisplayDrop}
                  aria-label={mediaType === 'video' ? 'Add video' : 'Add image'}
                >
                  <PlusIcon />
                  Add
                </button>
              )}
            </div>
          </div>

          <div className={s.galleryFadeTop} style={{ opacity: showTopFade ? 1 : 0 }} />
          <div className={s.galleryFadeBottom} style={{ opacity: showBottomFade ? 1 : 0 }} />

          {hasBulkBar && (
            <div className={s.bulkBarOverlay}>
              <div className={s.bulkBar}>
                <span className={s.bulkBarCount}>{selectedIds.size} selected</span>
                <div className={s.bulkBarSep} />
                <button type="button" className={`${s.bulkBarBtn} is-danger`} onClick={handleBulkDelete}>
                  <TrashIcon size={13} />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {hint && !readOnly && <p className={s.hint}>{hint}</p>}
      {error && <p className={s.error}>{error}</p>}

      {uploadModal}
    </div>
  )
}
